import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

const LEVEL = { warn: "bg-amber-500", info: "bg-sky-500", good: "bg-teal-500" };

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  if (!user) return null;

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const clickItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notif-bell" className="relative p-2 rounded-lg hover:bg-blue-50">
        <Bell className="w-5 h-5 text-blue-900" />
        {unread > 0 && <span data-testid="notif-count" className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-bold min-w-5 h-5 px-1 grid place-items-center rounded-full">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="font-semibold text-blue-950 text-sm">Notifications</p>
            {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-teal-600 hover:underline">Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && <p className="text-sm text-slate-400 text-center py-8">You're all caught up.</p>}
            {items.map((n) => (
              <button key={n.id} onClick={() => clickItem(n)} data-testid="notif-item" className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 border-b border-slate-50 ${!n.read ? "bg-blue-50/40" : ""}`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${LEVEL[n.level] || LEVEL.info}`} />
                <div><p className="text-sm font-medium text-blue-950">{n.title}</p><p className="text-xs text-slate-500">{n.body}</p></div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
